/**
 * @fileoverview Month grid for the home calendar.
 * @module components/HomeCalendar/CalendarGrid
 */

import { useCallback } from "react";
import styles from "./HomeCalendar.module.css";
import { getSpanningEventsForWeek } from "../../logic/homeCalendar";
import {
  dayKey,
  eventIndicatorKey,
  eventIndicatorTitle,
  getDayCellView,
  getSpanningEventStyle,
  spanningEventKey,
  weekKey,
} from "./homeCalendarLayout";

/**
 * One appointment chip. The title includes the time only when the item has one.
 * @param {Object} props
 * @param {Object} props.event
 */
const EventIndicator = ({ event }) => (
  <div
    className={`${styles.eventIndicator} ${styles[event.type]}`}
    title={eventIndicatorTitle(event)}
  >
    {event.time && (
      <span className={styles.eventTime}>
        {event.time} -{" "}
      </span>
    )}
    <span className={styles.eventName}>{event.name}</span>
  </div>
);

/**
 * Multi-day bar for one continuous segment of a week.
 * A current event opens the events page. A past event stays visible as
 * concluded and does not navigate.
 * @param {Object} props
 * @param {Object} props.spanningEvent
 * @param {Function} props.onOpenEvents
 * @param {Function} props.t
 */
const SpanningEventBar = ({ spanningEvent, onOpenEvents, t }) => {
  const isPast = Boolean(spanningEvent.isPast);
  const concludedLabel = t("components.homeCalendar.eventConcluded");

  return (
    <div
      className={`${styles.spanningEvent} ${styles[spanningEvent.type]} ${
        isPast ? styles.spanningEventPast : ""
      }`}
      style={getSpanningEventStyle(spanningEvent)}
      title={isPast ? concludedLabel : spanningEvent.name}
      data-tooltip={isPast ? concludedLabel : undefined}
      onClick={isPast ? undefined : onOpenEvents}
      aria-disabled={isPast || undefined}
    >
      <span className={styles.spanningEventName}>{spanningEvent.name}</span>
    </div>
  );
};

/**
 * A single day. Spanning bars are drawn by the week row, and they still
 * consume the visible-item limit, so the cell may show a "more" control.
 * @param {Object} props
 * @param {Object} props.day
 * @param {Array<Object>} props.week
 * @param {Object} props.calendarData
 * @param {string} props.language
 * @param {Function} props.onSelectDay
 * @param {Function} props.t
 */
const DayCell = ({ day, week, calendarData, language, onSelectDay, t }) => {
  const { itemsToShow, remainingCount, topPadding } = getDayCellView(
    day,
    week,
    calendarData,
    t,
    language,
  );
  const showEvents = itemsToShow.length > 0 || remainingCount > 0;
  const monthClass = day.isCurrentMonth ? styles.currentMonth : styles.otherMonth;
  const todayClass = day.isToday ? styles.today : "";
  const handleMoreClick = useCallback(() => {
    onSelectDay(day.date);
  }, [onSelectDay, day.date]);

  return (
    <div className={`${styles.dayCell} ${monthClass} ${todayClass}`}>
      <div className={styles.dayNumber}>{day.dayNumber}</div>
      {showEvents && (
        <div
          className={styles.eventsContainer}
          style={{ "--padding-top": `${topPadding}px` }}
        >
          {itemsToShow.map((event) => (
            <EventIndicator key={eventIndicatorKey(event)} event={event} />
          ))}
          {remainingCount > 0 && (
            <button
              type="button"
              className={styles.moreEventsIndicator}
              onClick={handleMoreClick}
            >
              {remainingCount} {t("common.more")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * One week row: spanning bars first, then the seven day cells.
 * Bar keys use the event and its segment, not the array index.
 * @param {Object} props
 * @param {Array<Object>} props.week
 * @param {Object} props.calendarData
 * @param {string} props.language
 * @param {Function} props.onSelectDay
 * @param {Function} props.onOpenEvents
 * @param {Function} props.t
 */
const WeekRow = ({
  week,
  calendarData,
  language,
  onSelectDay,
  onOpenEvents,
  t,
}) => {
  const spanningEvents = getSpanningEventsForWeek(week, calendarData);

  return (
    <div className={styles.weekRow}>
      {spanningEvents.map((spanningEvent) => (
        <SpanningEventBar
          key={spanningEventKey(spanningEvent)}
          spanningEvent={spanningEvent}
          onOpenEvents={onOpenEvents}
          t={t}
        />
      ))}
      {week.map((day) => (
        <DayCell
          key={dayKey(day)}
          day={day}
          week={week}
          calendarData={calendarData}
          language={language}
          onSelectDay={onSelectDay}
          t={t}
        />
      ))}
    </div>
  );
};

/**
 * Weekday headings and month rows. Collapsing the calendar only toggles
 * the grid class; the same days stay mounted.
 * @param {Object} props
 * @param {Object} props.state
 * @param {Object} props.actions
 */
const CalendarGrid = ({ state, actions }) => {
  const gridClassName = state.isExpanded ? "" : styles.collapsed;

  return (
    <div className={`${styles.calendarGrid} ${gridClassName}`}>
      <div className={styles.weekdayHeaders}>
        {state.dayNames.map((dayName) => (
          <div key={`weekday-${dayName}`} className={styles.weekdayHeader}>
            {dayName}
          </div>
        ))}
      </div>
      <div className={styles.monthGrid}>
        {state.monthDays.map((week) => (
          <WeekRow
            key={weekKey(week)}
            week={week}
            calendarData={state.calendarData}
            language={state.language}
            onSelectDay={actions.selectDay}
            onOpenEvents={actions.openEventsPage}
            t={state.t}
          />
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;
